import React, {useState, useEffect} from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import {Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {
  getHarvestRecords,
  createHarvestRecord,
  updateHarvestRecord,
  HarvestRecord,
  CreateHarvestRecordDto,
  UpdateHarvestRecordDto,
  updateCycle,
} from "../../api/pond";
import {useForm} from "../../utils/useForm";
import {FORM_VALIDATION_CONFIGS} from "../../utils/formValidation";
import FormField from "../../components/common/FormField";

interface HarvestTabProps {
  pondId: string;
}

const HarvestTab: React.FC<HarvestTabProps> = ({pondId}) => {
  const {t} = useTranslation();
  const [harvestRecords, setHarvestRecords] = useState<HarvestRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<HarvestRecord | null>(null);
  const [closeCycleModalOpen, setCloseCycleModalOpen] = useState(false);
  const [closeCycleDate, setCloseCycleDate] = useState("");

  const form = useForm({
    initialValues: {
      harvestDate: "",
      count: 0,
      totalWeight: 0,
      harvestType: "partial" as "partial" | "final",
      notes: "",
    },
    validationConfig: FORM_VALIDATION_CONFIGS.HARVEST_RECORD,
    onSubmit: async (values) => {
      try {
        if (editingRecord) {
          await updateHarvestRecord(editingRecord.id, values as UpdateHarvestRecordDto);
        } else {
          await createHarvestRecord(pondId, values as CreateHarvestRecordDto);
        }
        fetchHarvestRecords();
        handleCloseModal();
      } catch (err: any) {
        console.error("Error saving harvest record:", err);
        setError(err.response?.data?.message || "Failed to save harvest record");
        throw err;
      }
    },
    onError: (errors) => {
      console.log("Validation errors:", errors);
    },
  });

  useEffect(() => {
    fetchHarvestRecords();
  }, [pondId]);

  const fetchHarvestRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getHarvestRecords(pondId);
      setHarvestRecords(data.data || []);
    } catch (err) {
      setError("Failed to load harvest records");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (record?: HarvestRecord) => {
    if (record) {
      setEditingRecord(record);
      form.setValues({
        harvestDate: record.harvestDate.split("T")[0],
        count: record.count,
        totalWeight: record.totalWeight,
        harvestType: record.harvestType,
        notes: record.notes || "",
      });
    } else {
      setEditingRecord(null);
      form.reset();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
    form.reset();
  };

  const handleOpenCloseCycleModal = () => {
    setCloseCycleDate("");
    setCloseCycleModalOpen(true);
  };

  const handleCloseCycleModal = () => {
    setCloseCycleModalOpen(false);
    setCloseCycleDate("");
  };

  const handleCloseCycle = async () => {
    if (!closeCycleDate) {
      setError("Please select a closing date");
      return;
    }

    try {
      // Here you would typically call an API to close the cycle
      // For now, we'll just close the modal
      console.log("Closing cycle with date:", closeCycleDate);
      setCloseCycleModalOpen(false);
      setCloseCycleDate("");
      // TODO: Implement actual cycle closing logic
    } catch (err: any) {
      setError("Failed to close cycle");
    }
  };

  const calculateTotalHarvested = () => {
    return harvestRecords.reduce((sum, record) => sum + record.count, 0);
  };

  const calculateTotalHarvestedWeight = () => {
    return harvestRecords.reduce((sum, record) => sum + record.totalWeight, 0);
  };

  const calculateSurvivalNumber = (count: number, totalWeight: number) => {
    return count * totalWeight;
  };

  const getHarvestTypeColor = (type: string) => {
    return type === "final" ? "error" : "primary";
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Card sx={{minWidth: 200, flex: 1}}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Harvest Records
            </Typography>
            <Typography variant="h4">{harvestRecords.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{minWidth: 200, flex: 1}}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Shrimp Harvested
            </Typography>
            <Typography variant="h4">{calculateTotalHarvested().toLocaleString()}</Typography>
          </CardContent>
        </Card>
        <Card sx={{minWidth: 200, flex: 1}}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Weight Harvested
            </Typography>
            <Typography variant="h4">{calculateTotalHarvestedWeight().toFixed(2)} kg</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Button 
          variant="outlined" 
          color="secondary" 
          startIcon={<CloseIcon />} 
          onClick={handleOpenCloseCycleModal}
        >
          {t("Close Cycle")}
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
          {t("Add Harvest Record")}
        </Button>
      </Box>

      {/* Harvest Records Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("Date")}</TableCell>
              <TableCell align="right">{t("Count")}</TableCell>
              <TableCell align="right">{t("Total Weight (kg)")}</TableCell>
              <TableCell align="right">{t("Survival Number")}</TableCell>
              <TableCell>{t("Type")}</TableCell>
              <TableCell>{t("Notes")}</TableCell>
              <TableCell align="center">{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {harvestRecords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">{t("No harvest records found")}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              harvestRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{new Date(record.harvestDate).toLocaleDateString()}</TableCell>
                  <TableCell align="right">{record.count.toLocaleString()}</TableCell>
                  <TableCell align="right">{record.totalWeight.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    {record.survivalNumber ? record.survivalNumber.toFixed(2) : calculateSurvivalNumber(record.count, record.totalWeight).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Chip label={record.harvestType} color={getHarvestTypeColor(record.harvestType) as any} size="small" />
                  </TableCell>
                  <TableCell>{record.notes || "-"}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenModal(record)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Harvest Record Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRecord ? t("Edit Harvest Record") : t("Add Harvest Record")}</DialogTitle>
        <form onSubmit={form.handleSubmit}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label={t("Harvest Date")}
                type="date"
                name="harvestDate"
                value={form.values.harvestDate}
                onChange={form.handleChange("harvestDate")}
                fullWidth
                required
                error={!!form.errors.harvestDate}
                helperText={form.errors.harvestDate}
                InputLabelProps={{shrink: true}}
              />

              <TextField
                label={t("Count")}
                type="number"
                name="count"
                value={form.values.count}
                onChange={form.handleChange("count")}
                fullWidth
                required
                error={!!form.errors.count}
                helperText={form.errors.count}
                inputProps={{min: 1}}
              />

              <TextField
                label={t("Total Weight (kg)")}
                type="number"
                name="totalWeight"
                value={form.values.totalWeight}
                onChange={form.handleChange("totalWeight")}
                fullWidth
                required
                error={!!form.errors.totalWeight}
                helperText={form.errors.totalWeight}
                inputProps={{min: 0, step: 0.01}}
              />

              {/* Display calculated Survival Number */}
              {form.values.count > 0 && form.values.totalWeight > 0 && (
                <TextField
                  label={t("Survival Number")}
                  type="number"
                  value={calculateSurvivalNumber(form.values.count, form.values.totalWeight).toFixed(2)}
                  fullWidth
                  disabled
                  helperText={t("Calculated automatically (Count × Total Weight)")}
                />
              )}

              <FormControl fullWidth required error={!!form.errors.harvestType}>
                <InputLabel>{t("Harvest Type")}</InputLabel>
                <Select
                  name="harvestType"
                  value={form.values.harvestType}
                  onChange={(e) => form.handleChange("harvestType")(e as any)}
                  label={t("Harvest Type")}>
                  <MenuItem value="partial">{t("Partial")}</MenuItem>
                  <MenuItem value="final">{t("Final")}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label={t("Notes")}
                name="notes"
                value={form.values.notes}
                onChange={form.handleChange("notes")}
                fullWidth
                multiline
                rows={3}
                error={!!form.errors.notes}
                helperText={form.errors.notes}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t("Cancel")}</Button>
            <Button type="submit" variant="contained" disabled={form.isSubmitting}>
              {form.isSubmitting ? <CircularProgress size={20} /> : editingRecord ? t("Update") : t("Add")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Close Cycle Modal */}
      <Dialog open={closeCycleModalOpen} onClose={handleCloseCycleModal} maxWidth="sm" fullWidth>
        <DialogTitle>{t("Close Cycle")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="textSecondary">
              {t("Enter the closing date for this cycle. This will mark the end of the current production cycle.")}
            </Typography>
            <Divider />
            <TextField
              label={t("Closing Date")}
              type="date"
              value={closeCycleDate}
              onChange={(e) => setCloseCycleDate(e.target.value)}
              fullWidth
              required
              InputLabelProps={{shrink: true}}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCycleModal}>{t("Cancel")}</Button>
          <Button onClick={handleCloseCycle} variant="contained" disabled={!closeCycleDate}>
            {t("Close Cycle")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HarvestTab;
